import {
    type Request,
    type CreateRequestDto,
    type RequestHistoryEntry,
    RequestSource,
    RequestStatus,
    ShiftPreference,
} from '@features/requests/types';
import { normalizeToStartOfDay } from '@shared/utils';

/**
 * Request Model
 * Static methods for request operations and validations
 */
export class RequestModel {
    /**
     * Creates a new request in DRAFT status
     */
    static create(dto: CreateRequestDto, employeeId: string): Request {
        const now = new Date();
        return {
            id: this.generateId(),
            type: dto.type,
            status: RequestStatus.DRAFT,
            startDate: dto.startDate,
            endDate: dto.endDate,
            notes: dto.notes,
            attachments: dto.attachments ?? [],
            shiftPreference: dto.shiftPreference,
            excludedDates: dto.excludedDates,
            createdAt: now,
            updatedAt: now,
            history: [],
            employeeId,
        };
    }

    /**
     * Validates basic request data
     */
    static validate(dto: CreateRequestDto): { valid: boolean; error?: string } {
        // Validate type
        const validSources = Object.values(RequestSource);
        if (!validSources.includes(dto.type)) {
            return {
                valid: false,
                error: 'Ugyldig søknadstype. Velg Ferie, Betalt permisjon eller Skiftpreferanse.',
            };
        }

        // Validate dates
        if (!dto.startDate || !dto.endDate) {
            return {
                valid: false,
                error: 'Startdato og sluttdato er påkrevd.',
            };
        }

        if (dto.endDate < dto.startDate) {
            return {
                valid: false,
                error: 'Sluttdato må være lik eller etter startdato.',
            };
        }

        // Validate shift preference for SHIFT_PREFERENCE (API source: WISH)
        if (dto.type === RequestSource.WISH) {
            if (!dto.shiftPreference) {
                return {
                    valid: false,
                    error: 'Skiftpreferanse er påkrevd for Skiftpreferanse-søknader.',
                };
            }
            const validShifts = Object.values(ShiftPreference);
            if (!validShifts.includes(dto.shiftPreference)) {
                return {
                    valid: false,
                    error: 'Ugyldig skiftpreferanse. Velg Morgen, Ettermiddag, Kveld, Helg eller Annet.',
                };
            }
        }

        return { valid: true };
    }

    /**
     * Checks if a request can be edited
     * DRAFT and DECLINED status can be edited
     */
    static canEdit(request: Request): boolean {
        return (
            request.status === RequestStatus.DRAFT ||
            request.status === RequestStatus.REJECTED
        );
    }

    /**
     * Checks if a request can be cancelled
     * Only SUBMITTED status can be cancelled
     */
    static canCancel(request: Request): boolean {
        return request.status === RequestStatus.SUBMITTED;
    }

    /**
     * Checks if a request can be submitted
     * Only DRAFT status can be submitted
     */
    static canSubmit(request: Request): boolean {
        return request.status === RequestStatus.DRAFT;
    }

    /**
     * Checks if a request can be resubmitted after being declined
     * Only REJECTED status can be resubmitted
     */
    static canResubmit(request: Request): boolean {
        return request.status === RequestStatus.REJECTED;
    }

    /**
     * Checks if a request is in a terminal state (cannot be modified)
     */
    static isTerminalState(request: Request): boolean {
        return (
            request.status === RequestStatus.APPROVED ||
            request.status === RequestStatus.REJECTED ||
            request.status === RequestStatus.CANCELLED ||
            request.status === RequestStatus.MODIFIED
        );
    }

    /**
     * Checks if a request blocks dates for new requests
     * SUBMITTED, APPROVED, and MODIFIED requests block dates
     */
    static isBlocking(request: Request): boolean {
        return (
            request.status === RequestStatus.SUBMITTED ||
            request.status === RequestStatus.APPROVED ||
            request.status === RequestStatus.MODIFIED
        );
    }

    /**
     * Generates a unique ID for requests
     */
    private static generateId(): string {
        return `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    }

    /**
     * Adds a history entry to a request
     */
    static addHistoryEntry(
        request: Request,
        fromStatus: RequestStatus,
        toStatus: RequestStatus,
        changedBy: string,
        notes?: string,
    ): Request {
        const newEntry: RequestHistoryEntry = {
            timestamp: new Date(),
            fromStatus,
            toStatus,
            changedBy,
            notes,
        };

        return {
            ...request,
            history: [...request.history, newEntry],
            status: toStatus,
            updatedAt: new Date(),
        };
    }

    /**
     * Checks if two date ranges overlap
     */
    static datesOverlap(
        start1: Date,
        end1: Date,
        start2: Date,
        end2: Date,
    ): boolean {
        const s1 = normalizeToStartOfDay(start1);
        const e1 = normalizeToStartOfDay(end1);
        const s2 = normalizeToStartOfDay(start2);
        const e2 = normalizeToStartOfDay(end2);

        return s1 <= e2 && s2 <= e1;
    }

    /**
     * Checks if a date is within excluded periods
     */
    static isDateExcluded(date: Date, excludedPeriods?: Date[]): boolean {
        if (!excludedPeriods || excludedPeriods.length === 0) {
            return false;
        }

        const targetDate = normalizeToStartOfDay(date);

        return excludedPeriods.some((period): boolean => {
            const periodDate = normalizeToStartOfDay(period);
            return targetDate.getTime() === periodDate.getTime();
        });
    }

    /**
     * Checks if a date range overlaps with excluded periods
     */
    static hasExcludedDates(
        startDate: Date,
        endDate: Date,
        excludedPeriods?: Date[],
    ): boolean {
        if (!excludedPeriods || excludedPeriods.length === 0) {
            return false;
        }

        const start = normalizeToStartOfDay(startDate);
        const end = normalizeToStartOfDay(endDate);

        const excludedDates = excludedPeriods.map(normalizeToStartOfDay);

        for (
            let date = new Date(start);
            date <= end;
            date.setDate(date.getDate() + 1)
        ) {
            const current = normalizeToStartOfDay(date);
            if (
                excludedDates.some(
                    (excluded): boolean =>
                        excluded.getTime() === current.getTime(),
                )
            ) {
                return true;
            }
        }

        return false;
    }

    /**
     * Validates if a request can be edited/submitted based on deadline
     * and excluded periods.
     *
     * The deadline is the cutoff for submitting: once it has passed the
     * request can no longer be submitted (`validateDeadline` defaults
     * to true; saving a draft skips the check). The requested dates may
     * legitimately be after the deadline, so they are not validated
     * against it.
     */
    static validateRequestTiming(
        request: Request,
        options?: {
            validateDeadline?: boolean;
            validateExcludedPeriods?: boolean;
        },
    ): { valid: boolean; error?: string } {
        const validateDeadline = options?.validateDeadline ?? true;
        const validateExcludedPeriods =
            options?.validateExcludedPeriods ?? true;
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        // Skip validation for local requests (employeeId is undefined or null)
        // These are draft requests created locally and not yet synced with API
        if (!request.employeeId) {
            return { valid: true };
        }

        // Check if the submission deadline has passed. The requested
        // dates may legitimately be after the deadline (the deadline is
        // only the cutoff for submitting), so they are not checked here.
        if (validateDeadline && request.deadline) {
            const deadlineDate = new Date(request.deadline);
            deadlineDate.setHours(0, 0, 0, 0);

            if (now > deadlineDate) {
                return {
                    valid: false,
                    error: 'Fristen for denne søknaden har utgått.',
                };
            }
        }

        // Check if selected dates are in excluded periods
        if (
            validateExcludedPeriods &&
            request.excludedPeriods &&
            request.excludedPeriods.length > 0 &&
            request.startDate &&
            request.endDate
        ) {
            if (
                this.hasExcludedDates(
                    request.startDate,
                    request.endDate,
                    request.excludedPeriods,
                )
            ) {
                return {
                    valid: false,
                    error: 'Søknadsdatoer inkluderer blokkerte perioder.',
                };
            }
        }

        return { valid: true };
    }
}
